import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { selectUser } from "../../store/authSlice";

const ORG_ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: "1", label: "Super Admin" },
  { value: "2", label: "Admin" },
  { value: "3", label: "Manager" },
  { value: "4", label: "Team lead" },
  { value: "5", label: "Agent" },
];

function personLabel(u) {
  if (!u) return "—";
  return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "—";
}

function personWithRole(u) {
  if (!u) return "—";
  return `${personLabel(u)} (${u.role?.name || "—"})`;
}

function TreeLine({ user, onOpen }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 text-sm">
      <span className="min-w-0 truncate text-gray-900">
        <span className="font-medium">{personLabel(user)}</span>
        <span className="text-gray-500"> ({user.role?.name || "—"})</span>
      </span>
      <button
        type="button"
        onClick={() => onOpen(user.user_id)}
        className="shrink-0 text-xs font-semibold text-blue-600 hover:underline"
      >
        Open
      </button>
    </div>
  );
}

function AgentLine({ user, onOpen }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1 pl-3 text-xs text-gray-800 border-l border-gray-200">
      <span className="min-w-0 truncate">
        <span className="font-medium">{personLabel(user)}</span>
        <span className="text-gray-500"> ({user.role?.name || "—"})</span>
      </span>
      <button type="button" onClick={() => onOpen(user.user_id)} className="shrink-0 text-blue-600 font-semibold hover:underline">
        Open
      </button>
    </div>
  );
}

function ReportingLineCell({ u }) {
  const segments = [
    { label: "Team lead", person: u.org_team_lead },
    { label: "Manager", person: u.org_manager },
    { label: "Admin", person: u.org_admin },
    { label: "Super Admin", person: u.org_super_admin },
  ].filter((s) => s.person?.user_id);
  if (segments.length === 0) {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <div className="text-xs text-gray-800 space-y-0.5 max-w-[260px]">
      {segments.map(({ label, person }) => {
        const t = personWithRole(person);
        return (
          <div key={label} className="truncate" title={`${t}`}>
            <span className="text-gray-500">{label}</span>
            <span className="text-gray-400"> · </span>
            <span>{personLabel(person)}</span>
            <span className="text-gray-500"> ({person.role?.name || "—"})</span>
          </div>
        );
      })}
    </div>
  );
}

function ChainRow({ title, user }) {
  if (!user) return null;
  return (
    <div className="flex items-center justify-between gap-2 border-b border-gray-100 py-2 text-sm last:border-0">
      <span className="text-gray-500 shrink-0">{title}</span>
      <span className="min-w-0 truncate text-right text-gray-900">
        {personLabel(user)} <span className="text-gray-500">({user.role?.name || "—"})</span>
      </span>
    </div>
  );
}

function TreeSection({ title, children }) {
  return (
    <div className="mt-4">
      <h4 className="text-xs font-semibold text-gray-500 mb-2">{title}</h4>
      <div className="border border-gray-200 rounded-lg bg-white px-3 py-2">{children}</div>
    </div>
  );
}

export default function OrgOverview() {
  const authUser = useSelector(selectUser);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelUserId, setPanelUserId] = useState(null);
  const [panel, setPanel] = useState(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [rebuildBusy, setRebuildBusy] = useState(false);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedRole, setAppliedRole] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/organization/users", {
        params: {
          search: appliedSearch.trim() || undefined,
          role_id: appliedRole || undefined,
          page,
          limit,
        },
      });
      setRows(res.data?.users || []);
      setMeta(
        res.data?.meta || {
          total: 0,
          page,
          limit,
          totalPages: 0,
        }
      );
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load users");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, appliedRole, page, limit]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const openPanel = async (userId) => {
    setPanelUserId(userId);
    setPanelLoading(true);
    try {
      const res = await api.get(`/admin/organization/panel/${userId}`);
      setPanel(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load organisation view");
      setPanel(null);
    } finally {
      setPanelLoading(false);
    }
  };

  const rebuild = async () => {
    setRebuildBusy(true);
    try {
      // Recomputes hierarchy fields for the whole org synchronously — the
      // heaviest of the three flagged batch operations, so it gets the most
      // headroom. 180s override, scoped to this call only.
      await api.post("/admin/organization/rebuild-org-cache", {}, { timeout: 180000 });
      toast.success("Hierarchy fields rebuilt");
      await loadUsers();
      if (panelUserId) await openPanel(panelUserId);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Rebuild failed");
    } finally {
      setRebuildBusy(false);
    }
  };

  const renderTree = (tree) => {
    if (!tree) return <p className="text-sm text-gray-500">No tree data.</p>;
    const t = tree.type;

    if (t === "super_admin") {
      const admins = tree.admins || [];
      return (
        <TreeSection title="Admins">
          <div className="divide-y divide-gray-100 -mx-3">
            {admins.map(({ admin, managers }) => (
              <div key={admin.user_id} className="py-3 first:pt-0">
                <TreeLine user={admin} onOpen={openPanel} />
                {(managers || []).map(({ manager, team_leads }) => (
                  <div key={manager.user_id} className="mt-2 ml-2 pl-3 border-l border-gray-200">
                    <TreeLine user={manager} onOpen={openPanel} />
                    {(team_leads || []).map(({ team_lead, agents }) => (
                      <div key={team_lead.user_id} className="mt-2 ml-2 pl-3 border-l border-gray-200">
                        <TreeLine user={team_lead} onOpen={openPanel} />
                        {(agents || []).length > 0 && (
                          <div className="mt-1.5 space-y-0.5">
                            {(agents || []).map((a) => (
                              <AgentLine key={a.user_id} user={a} onOpen={openPanel} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </TreeSection>
      );
    }

    if (t === "admin") {
      const managers = tree.managers || [];
      return (
        <TreeSection title="Managers and below">
          <div className="divide-y divide-gray-100 -mx-3">
            {managers.map(({ manager, team_leads }) => (
              <div key={manager.user_id} className="py-3 first:pt-0">
                <TreeLine user={manager} onOpen={openPanel} />
                {(team_leads || []).map(({ team_lead, agents }) => (
                  <div key={team_lead.user_id} className="mt-2 ml-2 pl-3 border-l border-gray-200">
                    <TreeLine user={team_lead} onOpen={openPanel} />
                    {(agents || []).length > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        {(agents || []).map((a) => (
                          <AgentLine key={a.user_id} user={a} onOpen={openPanel} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </TreeSection>
      );
    }

    if (t === "manager") {
      const team_leads = tree.team_leads || [];
      return (
        <TreeSection title="Team leads & agents">
          <div className="divide-y divide-gray-100 -mx-3">
            {team_leads.map(({ team_lead, agents }) => (
              <div key={team_lead.user_id} className="py-3 first:pt-0">
                <TreeLine user={team_lead} onOpen={openPanel} />
                {(agents || []).length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {(agents || []).map((a) => (
                      <AgentLine key={a.user_id} user={a} onOpen={openPanel} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </TreeSection>
      );
    }

    if (t === "team_lead") {
      const agents = tree.agents || [];
      return (
        <TreeSection title="Agents">
          <div className="space-y-0.5 -mx-3 px-0 py-2">
            {(agents || []).map((a) => (
              <AgentLine key={a.user_id} user={a} onOpen={openPanel} />
            ))}
          </div>
        </TreeSection>
      );
    }

    return <p className="text-sm text-gray-600">Reporting chain is above.</p>;
  };

  if (Number(authUser?.role_id) !== 1) {
    return (
      <div className="p-6">
        <p className="text-gray-600">This view is available to Super Admin only.</p>
      </div>
    );
  }

  const goPrev = () => {
    if (meta.page <= 1) return;
    setPage((p) => p - 1);
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    setPage((p) => p + 1);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 min-h-[calc(100vh-8rem)]">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Organization view</h1>
            <p className="text-sm text-gray-600 mt-1">Super Admin · reporting line and org tree.</p>
          </div>
          {/* <button
            type="button"
            onClick={rebuild}
            disabled={rebuildBusy}
            className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
          >
            {rebuildBusy ? "Rebuilding…" : "Rebuild hierarchy cache"}
          </button> */}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedSearch(search);
            setAppliedRole(roleFilter);
            setPage(1);
          }}
          className="mt-4 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3"
        >
          <div className="w-full sm:min-w-[200px] sm:flex-1">
            <label className="text-sm font-medium block text-gray-700">Search</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email, mobile…"
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black/20"
            />
          </div>
          <div className="w-full sm:min-w-[160px] sm:w-auto">
            <label className="text-sm font-medium block text-gray-700">Role</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black/20"
            >
              {ORG_ROLE_OPTIONS.map((o) => (
                <option key={o.value || "all"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full sm:min-w-[100px] sm:w-auto">
            <label className="text-sm font-medium block text-gray-700">Rows</label>
            <select
              value={String(limit)}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button type="submit" className="rounded-lg bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90">
              Apply
            </button>
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setRoleFilter("");
                setAppliedSearch("");
                setAppliedRole("");
                setPage(1);
              }}
              className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
            >
              Reset
            </button>
          </div>
        </form>

        <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-800">
            <span>Organisation users</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goPrev}
                disabled={meta.page <= 1 || loading}
                className="rounded border px-2 py-1 text-xs disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-xs text-gray-600">
                {meta.page} / {meta.totalPages || 1} · {meta.total} total
              </span>
              <button
                type="button"
                onClick={goNext}
                disabled={meta.page >= meta.totalPages || loading || meta.totalPages === 0}
                className="rounded border px-2 py-1 text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr className="text-left text-gray-700">
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Email</th>
                    <th className="px-4 py-2 font-medium">Reporting line</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr
                      key={u.user_id}
                      className={`cursor-pointer border-t border-gray-100 hover:bg-gray-50/80 ${panelUserId === u.user_id ? "bg-blue-50/50" : ""}`}
                      onClick={() => openPanel(u.user_id)}
                      title="Open org panel"
                    >
                      <td className="px-4 py-2.5 align-top">
                        <span className="font-medium text-gray-900">{personLabel(u)}</span>
                        <span className="text-gray-500"> ({u.role?.name || "—"})</span>
                      </td>
                      <td className="px-4 py-2.5 align-top text-gray-600">{u.email}</td>
                      <td className="px-4 py-2.5 align-top">
                        <ReportingLineCell u={u} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && !loading && (
                <div className="p-6 text-sm text-gray-500 text-center">No users match your filters.</div>
              )}
            </div>
          )}
        </div>
      </div>

      <aside className="lg:w-[340px] shrink-0 rounded-lg border border-gray-200 bg-gray-50/80 p-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
        {!panelUserId && <p className="text-sm text-gray-500">Click a row to see reporting chain and org tree.</p>}
        {panelLoading && <p className="text-sm text-gray-600">Loading…</p>}
        {panel && !panelLoading && (
          <>
            <h2 className="text-base font-bold text-gray-900">
              {personLabel(panel.user)}
              <span className="font-normal text-gray-500"> ({panel.user?.role?.name || "—"})</span>
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{panel.user?.email}</p>

            <div className="mt-3 rounded-md border border-gray-200 bg-white p-3">
              <h3 className="text-xs font-semibold text-gray-500 mb-1">Chain</h3>
              <ChainRow title="Super Admin" user={panel.org_chain?.super_admin} />
              <ChainRow title="Admin" user={panel.org_chain?.admin} />
              <ChainRow title="Manager" user={panel.org_chain?.manager} />
              <ChainRow title="Team lead" user={panel.org_chain?.team_lead} />
            </div>

            {renderTree(panel.tree)}
          </>
        )}
      </aside>
    </div>
  );
}
